import { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import { makeStyles, Button, Card, CardContent, Typography, IconButton, Popover, Tooltip, CircularProgress } from '@ellucian/react-design-system/core';
import { 
    spacing20,
    spacing30,
    spacing40,
    spacing10,
    fontSizeSmall,
    sizingMedium,
    colorTextError,
    colorTextAlertError,
    colorTextNeutral600,
    colorTextNeutral200,
    fontSizeDefault,
    colorCtaBlueBase,
    colorBrandCtaBase,
    colorGlobalBorderDefault,
    colorBackgroundAlertError,
    borderWidthThin,
    borderWidthThick
} from '@ellucian/react-design-system/core/styles/tokens';
import { batch, useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import get from 'lodash/get';
import { validateField, setTouched, setFocused, setValue } from '@nsr/react-form';
import { useApi } from '../ApiContext/ApiContext';
import { setApplicationValue, deleteApplicationValue } from '../ApplicationFormLayout/redux/slices/applicationSlice';
import { useIntl } from 'react-intl';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import useRendersCount from '@hedtech/userenderscount';
import { CONSTANTS } from '../Helpers/Configuration';
import { 
    formatFileSize, 
    validateFile as validateFileHelper,
    generateTempId,
    getAcceptAttribute,
    formatFileTypes
} from '../../utilities/fileHelpers';
import { normalizeFileMetadata, findFileMetadata } from '../../utilities/fileUploadService';
import useApplicationForm from '../ApplicationFormLayout/useApplicationForm';
import { SUPPORTED_FEATURES } from '@nsr/applicant-apply-shared/src/helpers/featureFlags';
import { DEFAULT_DOCUMENT_REVIEW_STATUS } from '../Helpers/documentReviewStatusConfig';
import { Icon } from '@ellucian/ds-icons/lib';

const useStyles = makeStyles({
    name: 'FormUploadQuestion'
})((theme) => ({
    container: {
        width: '100%',
        maxWidth: '100%',
        [theme.breakpoints.up('sm')]: {
            width: '100%',
            maxWidth: '100%'
        },
        [theme.breakpoints.up('md')]: {
            width: '50%',
            maxWidth: '50%'
        }
    },
    card: {
        backgroundColor: colorTextNeutral200,
        padding: '0 !important',
        margin: 0
    },
    cardError: {
        border: `${borderWidthThin} solid ${colorTextAlertError || colorTextError}`,
        boxShadow: 'none'
    },
    compactCardContent: {
        padding: `${spacing40} ${spacing40} !important`,
        [theme.breakpoints.down('md')]: {
            padding: `${spacing30} ${spacing30} !important`
        },
        '&:last-child': {
            paddingBottom: `${spacing40} !important`
        }
    },
    contentLayout: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing40,
        margin: 0,
        padding: 0,
        alignItems: 'stretch',
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing30
        }
    },
    contentInfo: {
        flex: 1,
        textAlign: 'left',
        padding: 0
    },
    titleText: {
        marginBottom: spacing10,
        marginTop: '0 !important',
        fontWeight: 'medium',
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        lineHeight: 1.5,
        color: colorTextNeutral600,
        position: 'relative'
    },
    titleErrorIcon: {
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: `translateX(calc(-100% - ${spacing20})) translateY(-50%)`,
        color: colorTextError,
        marginRight: 0
    },
    helpIcon: {
        marginLeft: spacing10,
        padding: `${spacing30} !important`,
        minWidth: 'auto !important',
        minHeight: 'auto !important',
        '& svg': {
            fontSize: sizingMedium,
            lineHeight: 1
        },
        '&:focus-visible': {
            outline: `${borderWidthThick || borderWidthThin} solid currentColor`,
            outlineOffset: spacing20
        }
    },
    helpPopoverContent: {
        '&:focus': {
            outline: `${borderWidthThick || borderWidthThin} solid ${colorGlobalBorderDefault}`,
            outlineOffset: spacing20
        }
    },
    helpPopoverText: {
        padding: spacing30,
        maxWidth: '300px'
    },
    helpText: {
        marginBottom: spacing10,
        color: colorTextNeutral600
    },
    infoText: {
        marginTop: '0.75rem !important',
        marginBottom: spacing30,
        color: `${colorTextNeutral600} !important`,
        lineHeight: 1.4
    },
    helperText: {
        marginTop: spacing10,
        lineHeight: 1.4
    },
    helperTextContainer: {
        marginTop: '0.75rem'
    },
    helperTextError: {
        color: colorTextError,
        fontSize: fontSizeSmall
    },
    helperTextNormal: {
        color: colorTextNeutral600
    },
    requiredIndicator: {
        marginLeft: spacing20,
        color: colorTextError,
        fontWeight: 600
    },
    uploadSection: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'stretch',
        marginTop: spacing10,
        padding: 0,
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            width: 'auto'
        }
    },
    hiddenInput: {
        display: 'none'
    },
    fileListContainer: {
        marginTop: 0,
        padding: `0 ${spacing30} ${spacing20} 0`
    },
    fileList: {
        margin: `${spacing20} 0 0 0`,
        padding: 0,
        listStyle: 'none'
    },
    fileListItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: spacing20,
        marginLeft: spacing30,
        padding: `${spacing20} ${spacing30}`,
        borderBottom: `${borderWidthThin} solid ${colorGlobalBorderDefault}`
    },
    fileRowLeft: {
        display: 'grid',
        gridTemplateColumns: `${sizingMedium} 1fr`,
        columnGap: spacing20,
        rowGap: spacing10,
        width: '100%',
        alignItems: 'center'
    },
    fileRowIcon: {
        color: colorTextNeutral600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        alignSelf: 'center',
        '& svg': {
            fontSize: sizingMedium
        }
    },
    fileRowContent: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: spacing40,
        minWidth: 0
    },
    fileRowTitle: {
        fontWeight: 600,
        lineHeight: 1.5
    },
    fileRowError: {
        color: colorTextError,
        fontSize: fontSizeSmall,
        fontWeight: 'normal',
        display: 'block',
        lineHeight: 1.6,
        gridColumn: '1 / span 2',
        margin: 0
    },
    fileRowMeta: {
        marginLeft: spacing30,
        color: colorTextNeutral600,
        fontSize: fontSizeSmall,
        fontWeight: 'normal'
    },
    fileRowActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        width: '100%',
        gap: spacing10
    },
    deleteButtonUnboxed: {
        padding: spacing10,
        minWidth: 'auto',
        minHeight: 'auto',
        border: 'none',
        borderRadius: spacing10,
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:focus-visible': {
            outline: `${borderWidthThick || borderWidthThin} solid currentColor`,
            outlineOffset: spacing20
        }
    },
    fileListHeader: {
        marginBottom: 0,
        fontWeight: 'normal',
        marginLeft: 0,
        paddingLeft: spacing40 },
    fileListHeaderContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing30,
        marginTop: spacing30,
        marginBottom: spacing10,
        marginLeft: 0,
        paddingLeft: 0 },
    retentionInfoIcon: {
        padding: spacing20,
        margin: 0,
        minWidth: 'auto',
        minHeight: 'auto',
        '&:focus-visible': {
            outline: `${borderWidthThick || borderWidthThin} solid currentColor`,
            outlineOffset: spacing20
        }
    },
    errorContainer: {
        marginTop: '0.75rem',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing30
    },
    errorRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '0.75rem'
    },
    errorMessage: {
        marginBottom: spacing20,
        lineHeight: 1.4
    },
    fileErrorText: {
        color: colorTextError,
        fontSize: fontSizeSmall,
        lineHeight: 1.4,
        flex: 1
    },
    errorCloseButton: {
        padding: spacing40,
        minWidth: 'auto',
        minHeight: 'auto',
        border: 'none',
        '&:focus-visible': {
            outline: `${borderWidthThick || borderWidthThin} solid currentColor`,
            outlineOffset: spacing20
        }
    },
    deleteButton: {
        padding: spacing10,
        minWidth: sizingMedium,
        minHeight: sizingMedium,
        border: `${borderWidthThin} solid ${colorGlobalBorderDefault}`,
        '&:hover': {
            color: colorTextAlertError,
            backgroundColor: colorBackgroundAlertError
        }
    },
    uploadButton: {
        padding: `${'0.75rem'} ${spacing40} !important`,
        fontSize: fontSizeDefault,
        color: colorBrandCtaBase || colorCtaBlueBase,
        borderColor: colorCtaBlueBase,
        borderWidth: borderWidthThin,
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            width: 'auto'
        },
        '&:focus-visible': {
            outline: `${borderWidthThick || borderWidthThin} solid ${colorCtaBlueBase}`,
            outlineOffset: spacing20
        },
        '&.Mui-disabled': {
            color: theme.palette.text.white,
        }
    },
    uploadProgress: {
        padding: spacing30,
        minWidth: sizingMedium,
        minHeight: sizingMedium,
        border: 'none'
    },
    visuallyHidden: {
        position: 'absolute',
        left: '-10000px',
        top: 'auto',
        width: '1px',
        height: '1px',
        overflow: 'hidden'
    },
    tableHeader: {
        fontWeight: 600,
        padding: `${spacing20} ${spacing30}`,
        textAlign: 'left'
    } }));

const FormUploadQuestion = props => {
    const {
        formatFromStore = (value) => value,
        formatToStore = (value) => value,
        helperText: helperTextProp = null,
        label = '',
        name: nameProp,
        onBlur: onBlurProp,
        onFocus: onFocusProp,
        required = false,
        requiredErrorTextId = 'requiredFieldErrorTextId',
        validate: validateProp,
        disabled = false,
        question = {},
        previewMode = false,
        ...rest
    } = props;

    const { template, globalSettings } = useApplicationForm();
    const supplementalItemsEnabled = useMemo(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        return !!window.PRELOADED_STATE?.featureSupported?.[SUPPORTED_FEATURES.supplementalItems];
    }, []);
    const templateSupplementalStatus = template?.settings?.supplementalItemStatus;
    const tenantSupplementalStatus = globalSettings?.supplementalItemStatus;
    const supplementalItemStatus = supplementalItemsEnabled
        ? (templateSupplementalStatus ?? tenantSupplementalStatus)
        : undefined;
    useRendersCount(`FormUploadQuestion( ${nameProp} )`);
    const { formatMessage } = useIntl();
    const fileInputRef = useRef(null);
    const deleteButtonRefs = useRef({});
    const helpContentRef = useRef(null);
    const retentionContentRef = useRef(null);
    const dispatch = useDispatch();
    const { classes } = useStyles();

    // Extract questionId for question-keyed supplementalItems structure
    const questionId = question?.id;
    
    const [errorMessages, setErrorMessages] = useState([]);
    const [fileErrors, setFileErrors] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);
    const [retentionInfoAnchorEl, setRetentionInfoAnchorEl] = useState(null);
    const [uploadingFiles, setUploadingFiles] = useState({});
    const [deletingFiles, setDeletingFiles] = useState({});
    const [statusMessage, setStatusMessage] = useState('');

    const helpPopoverId = useMemo(() => `${nameProp}-help-popover`, [nameProp]);
    const helpPopoverLabelId = useMemo(() => `${nameProp}-help-popover-label`, [nameProp]);
    const retentionPopoverId = useMemo(() => `${nameProp}-retention-popover`, [nameProp]);
    const retentionPopoverLabelId = useMemo(() => `${nameProp}-retention-popover-label`, [nameProp]);

    const maxFiles = useMemo(() => 
        question.fileNumberLimit || CONSTANTS.supplementalQuestionDetails.fileDefaultLimit,
    [question.fileNumberLimit]
    );
    const maxSizeMB = useMemo(() => 
        question.fileSizeLimit || CONSTANTS.supplementalQuestionDetails.fileSizeLimit,
    [question.fileSizeLimit]
    );
    const allowedTypes = useMemo(() => 
        question.allowedFileTypes || CONSTANTS.allowedFileTypes,
    [question.allowedFileTypes]
    );
    const fileRetentionDays = useMemo(() => 
        question.fileRetentionDays || CONSTANTS.supplementalQuestionDetails.fileRetentionDays,
    [question.fileRetentionDays]
    );

    const hasUploadingFiles = useMemo(() => 
        Object.keys(uploadingFiles).length > 0,
    [uploadingFiles]
    );

    const uploadButtonText = useMemo(() => formatMessage({
        id: 'uploadQuestionSelectFileTextId'
    }), [formatMessage]);

    const selectAlwaysShowError = useMemo(() => {
        const firstKey = nameProp.split('.')[0];
        return createSelector(
            state => get(state.form.meta, `forceShowErrors.${firstKey}`),
            value => !!value
        );
    }, [nameProp]);

    const selectValue = useMemo(() => {
        return createSelector(
            state => get(state.form.values, nameProp),
            value => value ? formatFromStore(value) : []
        );
    }, [nameProp, formatFromStore]);

    const selectError = useMemo(() => {
        return createSelector(
            state => get(state.form.errors, nameProp),
            error => error
        );
    }, [nameProp]);

    const selectTouched = useMemo(() => {
        return createSelector(
            state => get(state.form.touched, nameProp),
            touched => touched
        );
    }, [nameProp]);

    const value = useSelector(selectValue);
    const error = useSelector(selectError);
    const touched = useSelector(selectTouched);
    const alwaysShowError = useSelector(selectAlwaysShowError);
    const appSupplementalItems = useSelector(state => state?.application?.supplementalItems || {});

    const selectSupplementalItemsMap = useMemo(() => {
        return createSelector(
            state => state?.application?.supplementalItemsMap,
            map => map || {}
        );
    }, []);
    
    const appSupplementalItemsMap = useSelector(selectSupplementalItemsMap);
    const applicationId = useSelector(state => state?.application?.id);
    const prospect = useSelector(state => state?.application?.prospect);
    const application = useSelector(state => state?.application);
    const api = useApi();
    const snackbar = useSnackbar();

    /**
     * Set file metadata in the question-keyed supplementalItems structure.
     * Creates the question entry with documentReviewStatus if it doesn't exist.
     * Files are stored as an array: supplementalItems[questionId].files = [...]
     */
    const setSupplementalFileMetadata = useCallback((documentId, metadata) => {
        if (!questionId) {
            // Fallback to old docId-keyed path if questionId is not available
            dispatch(setApplicationValue({ 
                name: `supplementalItems.${documentId}`, 
                value: metadata
            }));
            return;
        }
        
        // Get current question entry or create default structure
        const currentQuestionEntry = appSupplementalItems[questionId] || {};
        const currentFiles = Array.isArray(currentQuestionEntry.files) ? currentQuestionEntry.files : [];
        
        // Find existing file index or append
        const existingIndex = currentFiles.findIndex(f => f?.documentId === documentId);
        let updatedFiles;
        if (existingIndex >= 0) {
            // Update existing file entry
            updatedFiles = [...currentFiles];
            updatedFiles[existingIndex] = { ...updatedFiles[existingIndex], ...metadata, documentId };
        } else {
            // Add new file entry
            updatedFiles = [...currentFiles, { ...metadata, documentId }];
        }
        
        // Determine documentReviewStatus based on file upload status
        let documentReviewStatus = currentQuestionEntry.documentReviewStatus;
        
        // If this is a successful upload (status: 'success'), transition status to underReview
        if (metadata.status === 'success' && (!documentReviewStatus || documentReviewStatus === 'notReceived')) {
            documentReviewStatus = supplementalItemStatus || DEFAULT_DOCUMENT_REVIEW_STATUS;
        }
        
        // If no existing status, default based on template/tenant settings
        if (!documentReviewStatus) {
            documentReviewStatus = supplementalItemStatus || DEFAULT_DOCUMENT_REVIEW_STATUS;
        }
        
        // Build updated question entry with the new/updated files array
        const updatedQuestionEntry = {
            documentReviewStatus,
            files: updatedFiles
        };
        
        dispatch(setApplicationValue({ 
            name: `supplementalItems.${questionId}`, 
            value: updatedQuestionEntry
        }));
    }, [dispatch, questionId, appSupplementalItems, supplementalItemStatus]);

    /**
     * Delete file metadata from the question-keyed supplementalItems structure.
     * Removes the question entry if it has no more files.
     * Files are stored as an array: supplementalItems[questionId].files = [...]
     */
    const deleteSupplementalFileMetadata = useCallback((documentId) => {
        if (!questionId) {
            // Fallback to old docId-keyed path if questionId is not available
            dispatch(deleteApplicationValue(`supplementalItems.${documentId}`));
            return;
        }
        
        const currentQuestionEntry = appSupplementalItems[questionId];
        if (!currentQuestionEntry || !Array.isArray(currentQuestionEntry.files)) {
            // Question entry doesn't exist, try deleting at doc level for backward compat
            dispatch(deleteApplicationValue(`supplementalItems.${documentId}`));
            return;
        }
        
        // Filter out the file with the matching documentId
        const updatedFiles = currentQuestionEntry.files.filter(f => f?.documentId !== documentId);
        
        // If no more files, remove the entire question entry
        if (updatedFiles.length === 0) {
            dispatch(deleteApplicationValue(`supplementalItems.${questionId}`));
        } else {
            // Update the question entry without the deleted file
            const updatedQuestionEntry = {
                documentReviewStatus: currentQuestionEntry.documentReviewStatus,
                files: updatedFiles
            };
            dispatch(setApplicationValue({ 
                name: `supplementalItems.${questionId}`, 
                value: updatedQuestionEntry
            }));
        }
    }, [dispatch, questionId, appSupplementalItems]);

    const completedFiles = useMemo(() => {
        return (value || []).map(id => {
            const meta = findFileMetadata(id, appSupplementalItems, appSupplementalItemsMap);
            return meta || { id, name: id, size: 0, status: 'success' };
        });
    }, [value, appSupplementalItems, appSupplementalItemsMap]);

    // Derive error files list from consolidated fileErrors state
    const errorFilesList = useMemo(() => {
        return Object.entries(fileErrors).map(([id, errorData]) => ({
            id,
            name: errorData.file?.name || id,
            size: errorData.file?.size || 0,
            status: 'error',
            errorMessage: errorData.message,
            errorType: errorData.type
        }));
    }, [fileErrors]);

    const uploadedFiles = useMemo(() => {
        return [...completedFiles, ...errorFilesList];
    }, [completedFiles, errorFilesList]);

    const validFileCount = useMemo(() => {
        return completedFiles.length;
    }, [completedFiles]);

    const validate = useMemo(() => {
        const isRequired = value => {
            if (required && !value?.length) {
                return formatMessage({ id: requiredErrorTextId });
            }
            return undefined;
        };

        const composeValidators = (fns = []) => (value, allValues, meta) => {
            const validators = [isRequired, ...fns];
            for (const validator of validators) {
                const error = validator(value, allValues, meta);
                if (error) return error;
            }
            return undefined;
        };

        const validatorsArray = Array.isArray(validateProp) 
            ? validateProp 
            : validateProp ? [validateProp] : [];
        
        return composeValidators(validatorsArray);
    }, [formatMessage, required, validateProp, requiredErrorTextId]);

    /**
     * Initialize form field with file IDs from supplementalItems on mount or when supplementalItems changes.
     * This ensures that after a page refresh, uploaded files are restored from the persisted state.
     */
    useEffect(() => {
        if (!questionId || !appSupplementalItems) return;

        const questionEntry = appSupplementalItems[questionId];
        if (!questionEntry || !Array.isArray(questionEntry.files)) return;

        // Extract document IDs from the persisted files
        const fileIds = questionEntry.files
            .filter(f => f?.documentId && f?.status === 'success')
            .map(f => f.documentId);

        if (fileIds.length === 0) return;

        // Get current form value
        const currentValue = Array.isArray(value) ? value : [];
        
        // Only update if there's a difference (avoid unnecessary updates)
        const currentIdSet = new Set(currentValue);
        const persistedIdSet = new Set(fileIds);
        
        const isSame = 
            currentIdSet.size === persistedIdSet.size && 
            Array.from(currentIdSet).every(id => persistedIdSet.has(id));

        if (!isSame) {
            // Sync the form value with persisted file IDs
            dispatch(setValue({
                name: nameProp,
                value: formatToStore(fileIds) }));
        }
    }, [questionId, appSupplementalItems, nameProp, dispatch, value, formatToStore]);

    useEffect(() => {
        dispatch(validateField({ name: nameProp, validate, value }));
    }, [dispatch, nameProp, validate, value]);

    useEffect(() => {
        return () => {
            // Clear all state to prevent memory leaks
            setUploadingFiles({});
            setDeletingFiles({});
            setFileErrors({});
            setErrorMessages([]);
        };
    }, []);

    // Warn user before leaving page during file upload
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (hasUploadingFiles) {
                const message = formatMessage({ 
                    id: 'uploadQuestionUploadInProgressWarningTextId' 
                });
                event.preventDefault();
                event.returnValue = message; // Chrome requires returnValue
                return message; // For older browsers
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUploadingFiles, formatMessage]);

    const handleUploadClick = useCallback(() => {
        if (fileInputRef.current && !disabled) {
            fileInputRef.current.click();
        }
    }, [disabled]);

    const onChange = useCallback((newFiles) => {
        dispatch(setValue({
            name: nameProp,
            value: formatToStore(newFiles) }));
    }, [dispatch, formatToStore, nameProp]);

    const onBlur = useCallback(e => {
        batch(() => {
            dispatch(setTouched({ name: nameProp, value: true }));
            dispatch(setFocused({ name: nameProp, value: false }));
        });
        onBlurProp?.(e);
    }, [dispatch, onBlurProp, nameProp]);

    const onFocus = useCallback(e => {
        dispatch(setFocused({ name: nameProp, value: true }));
        onFocusProp?.(e);
    }, [dispatch, onFocusProp, nameProp]);

    const validateSingleFile = useCallback((file, validFiles) => {
        // Only check against successfully uploaded files, not files in error state
        const successfullyUploadedNames = uploadedFiles
            .filter(f => f && f.status !== 'error' && !f.errorMessage)
            .map(f => f?.name)
            .filter(Boolean);
        const allExistingFiles = [...successfullyUploadedNames, ...validFiles.map(v => v.name)];
        
        const result = validateFileHelper(file, {
            maxSizeMB,
            allowedTypes,
            existingFileNames: allExistingFiles
        });

        if (!result.valid) {
            // Translate generic error messages to use intl
            let errorId = 'uploadQuestionFileErrorTextId';
            const params = { fileName: file.name };
            
            if (result.error.includes('exceeds maximum size')) {
                errorId = 'uploadQuestionFileSizeErrorTextId';
                params.maxSize = maxSizeMB;
            } else if (result.error.includes('not allowed')) {
                errorId = 'uploadQuestionFileTypeErrorTextId';
                params.allowedTypes = allowedTypes.join(', ');
            } else if (result.error.includes('already exists')) {
                errorId = 'uploadQuestionDuplicateFileErrorTextId';
            }
            
            return { error: formatMessage({ id: errorId }, params) };
        }

        const documentId = generateTempId();
        const fileData = {
            id: documentId,
            documentId,
            name: file.name,
            size: file.size,
            type: file.type,
            uploadedAt: new Date().toISOString(),
            file
        };
        if (supplementalItemsEnabled && supplementalItemStatus) {
            fileData.supplementalItemStatus = supplementalItemStatus;
        }
        return { fileData };
    }, [formatMessage, maxSizeMB, allowedTypes, uploadedFiles, supplementalItemStatus, supplementalItemsEnabled]);

    const handleFileChange = useCallback(async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;
        
        // Clear previous error messages and file errors when user attempts a new upload
        setErrorMessages([]);
        setFileErrors(prev => {
            const filtered = {};
            const persistedIds = new Set(Array.isArray(value) ? value : []);
            Object.entries(prev).forEach(([id, error]) => {
                if (error.type === 'server') {
                    if (persistedIds.has(id)) {
                        filtered[id] = error;
                    }
                } else {
                    filtered[id] = error;
                }
            });
            return filtered;
        });
        
        const resetInput = () => {
            if (event.target) {
                event.target.value = '';
            }
        };
        
        if (validFileCount + files.length > maxFiles) {
            setErrorMessages([
                formatMessage({
                    id: 'uploadQuestionMaxFilesErrorTextId'
                }, { max: maxFiles })
            ]);
            resetInput();
            return;
        }

        const validFiles = [];
        const errors = [];

        files.forEach((file) => {
            const result = validateSingleFile(file, validFiles);
            
            if (result.error) {
                errors.push(result.error);
                // Add to consolidated error state
                const errorId = `error-${generateTempId()}`;
                setFileErrors(prev => ({
                    ...prev,
                    [errorId]: {
                        type: 'validation',
                        message: result.error,
                        file: { name: file.name, size: file.size, type: file.type }
                    }
                }));
            } else if (result.fileData) {
                validFiles.push(result.fileData);
            }
        });

        if (errors.length > 0) {
            resetInput();
            return;
        }

        const idsSet = new Set(Array.isArray(value) ? value : []);
        const syncIdsToStore = () => {
            const nextIds = Array.from(idsSet);
            dispatch(setValue({ name: nameProp, value: formatToStore(nextIds) }));
            dispatch(setApplicationValue({ name: `values.${nameProp}`, value: formatToStore(nextIds) }));
        };

        if (validFiles.length > 0) {
            validFiles.forEach(fileData => idsSet.add(fileData.documentId || fileData.id));
            syncIdsToStore();

            await Promise.all(validFiles.map(async (fileData) => {
                const documentId = fileData.documentId || fileData.id;
                setUploadingFiles(prev => ({ ...prev, [documentId]: 0 })); 
                
                try {
                    // Set uploading status
                    const initialMetadata = {
                        id: documentId,
                        documentId,
                        name: fileData.name,
                        size: fileData.size,
                        mimeType: fileData.type,
                        uploadedAt: new Date().toISOString(),
                        status: 'uploading'
                    };
                    if (supplementalItemsEnabled && supplementalItemStatus) {
                        initialMetadata.supplementalItemStatus = supplementalItemStatus;
                    }
                    setSupplementalFileMetadata(documentId, initialMetadata);

                    // Always use parallel chunked upload for all files
                    const metadata = {
                        name: fileData.name,
                        mimeType: fileData.type,
                        documentId
                    };
                    if (applicationId) metadata.appId = applicationId;
                    if (prospect) metadata.prospectId = prospect?.id || prospect;

                    const edmResponse = await api.edm.uploadFileChunked(fileData.file, metadata, {
                        chunkSize: 5 * 1024 * 1024, // 5MB chunks
                        concurrency: 3,
                        retryCount: 3,
                        onProgress: (progress) => {
                            // Update progress in supplementalItems
                            const progressMetadata = {
                                id: documentId,
                                documentId,
                                name: fileData.name,
                                size: fileData.size,
                                mimeType: fileData.type,
                                uploadedAt: new Date().toISOString(),
                                status: 'uploading',
                                progress: progress.percentage
                            };
                            if (supplementalItemsEnabled && supplementalItemStatus) {
                                progressMetadata.supplementalItemStatus = supplementalItemStatus;
                            }
                            setSupplementalFileMetadata(documentId, progressMetadata);
                            setUploadingFiles(prev => ({
                                ...prev,
                                [documentId]: typeof progress?.percentage === 'number' ? progress.percentage : prev[documentId] ?? 0
                            }));
                        }
                    });
                    
                    const edmFileId = edmResponse?.edmFileId || edmResponse?.id || edmResponse?.fileId || edmResponse?.data?.id;
                    if (documentId) {
                        const metadata = normalizeFileMetadata({ ...fileData, documentId, status: 'success' }, edmResponse);
                        metadata.edmFileId = edmFileId;
                        if (supplementalItemsEnabled && supplementalItemStatus) {
                            metadata.supplementalItemStatus = supplementalItemStatus;
                        }

                        setSupplementalFileMetadata(documentId, metadata);
                        
                        // Construct the updated supplementalItems manually to ensure we have the latest data
                        // rather than relying on Redux state which may not be updated yet
                        const currentQuestionEntry = appSupplementalItems[questionId] || {};
                        const currentFiles = Array.isArray(currentQuestionEntry.files) ? currentQuestionEntry.files : [];
                        
                        const existingIndex = currentFiles.findIndex(f => f?.documentId === documentId);
                        let updatedFiles;
                        if (existingIndex >= 0) {
                            updatedFiles = [...currentFiles];
                            updatedFiles[existingIndex] = { ...updatedFiles[existingIndex], ...metadata, documentId };
                        } else {
                            updatedFiles = [...currentFiles, { ...metadata, documentId }];
                        }
                        
                        // Determine documentReviewStatus for the question
                        let documentReviewStatus = currentQuestionEntry.documentReviewStatus;
                        if (metadata.status === 'success' && (!documentReviewStatus || documentReviewStatus === 'notReceived')) {
                            documentReviewStatus = supplementalItemStatus || DEFAULT_DOCUMENT_REVIEW_STATUS;
                        }
                        if (!documentReviewStatus) {
                            documentReviewStatus = supplementalItemStatus || DEFAULT_DOCUMENT_REVIEW_STATUS;
                        }
                        
                        // Build updated supplementalItems object
                        const updatedSupplementalItems = {
                            ...appSupplementalItems,
                            [questionId]: {
                                documentReviewStatus,
                                files: updatedFiles
                            }
                        };
                        
                        // Trigger autosave to persist supplementalItems to backend
                        if (applicationId && application && api?.applicationForms?.autosave) {
                            try {
                                const dataToSave = {
                                    ...application,
                                    supplementalItems: updatedSupplementalItems
                                };
                                await api.applicationForms.autosave(dataToSave);
                            } catch (err) {
                                console.warn('Failed to autosave after file upload', err);
                                // Don't fail the upload if autosave fails
                            }
                        }
                        
                        setStatusMessage(formatMessage({
                            id: 'uploadQuestionUploadSuccessTextId'
                        }, { fileName: fileData.name }));
                        setTimeout(() => setStatusMessage(''), 3000);

                        // Clear any errors for this file
                        setFileErrors(prev => {
                            if (!prev[documentId]) return prev;
                            const updated = { ...prev };
                            delete updated[documentId];
                            return updated;
                        });
                    } else {
                        // No EDM identifier returned - mark as error and roll back form values
                        idsSet.delete(documentId);
                        syncIdsToStore();
                        const uploadStatusText = formatMessage({
                            id: 'uploadDialogStatusUploading'
                        });
                        const errorMsg = formatMessage({
                            id: 'uploadQuestionServerErrorTextId'
                        });
                        deleteSupplementalFileMetadata(documentId);

                        setFileErrors(prev => ({
                            ...prev,
                            [documentId]: {
                                type: 'server',
                                message: errorMsg,
                                file: { name: fileData.name, size: fileData.size, type: fileData.type }
                            }
                        }));

                        setStatusMessage(`${uploadStatusText}: ${errorMsg}`);
                        setTimeout(() => setStatusMessage(''), 5000);
                    }
                } catch (err) {
                    console.error('Upload error', err);
                    const fallbackServerError = formatMessage({
                        id: 'uploadQuestionServerErrorTextId'
                    });
                    const errorMsg = err?.response?.data?.message 
                        || err?.message 
                        || fallbackServerError;
                    idsSet.delete(documentId);
                    syncIdsToStore();
                    deleteSupplementalFileMetadata(documentId);
                    setFileErrors(prev => ({
                        ...prev,
                        [documentId]: {
                            type: 'server',
                            message: errorMsg,
                            file: { name: fileData.name, size: fileData.size, type: fileData.type }
                        }
                    }));
                } finally {
                    setUploadingFiles(prev => {
                        const updated = { ...prev };
                        delete updated[documentId];
                        return updated;
                    });
                }
            }));
            // Clear validation errors on successful upload start
            setErrorMessages([]);
        }

        resetInput();
    }, [formatMessage, maxFiles, validateSingleFile, validFileCount, api, dispatch, nameProp, formatToStore, value, applicationId, application, prospect, supplementalItemStatus, supplementalItemsEnabled, setSupplementalFileMetadata, deleteSupplementalFileMetadata]);
                    
    const handleFileDelete = useCallback(async (fileId) => {
        try {
            setDeletingFiles(prev => ({ ...prev, [fileId]: true }));
            
            // Clear any error for this file (consolidated)
            setFileErrors(prev => {
                if (!prev[fileId]) return prev;
                const updated = { ...prev };
                delete updated[fileId];
                return updated;
            });
            
            // Check if this is an error file (validation or server error)
            if (String(fileId).startsWith('error-')) {
                deleteSupplementalFileMetadata(fileId);
                setDeletingFiles(prev => {
                    const updated = { ...prev };
                    delete updated[fileId];
                    return updated;
                });
                return;
            }
            const existingIds = Array.isArray(value) ? [...value] : [];
            const newIds = existingIds.filter(id => id !== fileId);
            onChange(newIds);
            
            // Update values in Redux
            dispatch(setApplicationValue({ 
                name: `values.${nameProp}`, 
                value: formatToStore(newIds) 
            }));
            // Remove metadata using the question-keyed helper
            deleteSupplementalFileMetadata(fileId);
            const metadata = findFileMetadata(fileId, appSupplementalItems, appSupplementalItemsMap);

            if (api?.edm?.deleteFile && metadata?.status !== 'error' && !fileErrors[fileId]) {
                const edmFileId = metadata?.edmFileId || metadata?.id || fileId;
                await api.edm.deleteFile(edmFileId);
            }
            
            // Announce successful deletion
            const deleteSuccessMsg = formatMessage({
                id: 'uploadQuestionDeleteSuccessTextId'
            });
            setStatusMessage(deleteSuccessMsg);
            setTimeout(() => setStatusMessage(''), 3000);
            
            // Manage focus after deletion
            const currentIndex = uploadedFiles.findIndex(f => f.id === fileId);
            const nextIndex = currentIndex < uploadedFiles.length - 1 ? currentIndex + 1 : currentIndex - 1;
            
            // Focus next file delete button, previous file, or upload button
            setTimeout(() => {
                if (nextIndex >= 0 && uploadedFiles[nextIndex]) {
                    const nextFileId = uploadedFiles[nextIndex].id;
                    deleteButtonRefs.current[nextFileId]?.focus();
                } else if (fileInputRef.current) {
                    // No more files, focus the upload button
                    const uploadButton = fileInputRef.current.nextElementSibling;
                    uploadButton?.focus();
                }
            }, 50);
            
            // Clear deleting state
            setDeletingFiles(prev => {
                const updated = { ...prev };
                delete updated[fileId];
                return updated;
            });
        } catch (err) {
            // Clear deleting state on error
            setDeletingFiles(prev => {
                const updated = { ...prev };
                delete updated[fileId];
                return updated;
            });

            snackbar.error(formatMessage({ id: 'uploadDeleteFileErrorTextId' }));
            console.error('Failed to delete file from EDM', err);
        }
    }, [value, onChange, dispatch, api, nameProp, formatToStore, snackbar, formatMessage, appSupplementalItems, appSupplementalItemsMap, uploadedFiles, deleteSupplementalFileMetadata]);

    const shouldShowError = useMemo(() => 
        error && (touched || alwaysShowError),
    [error, touched, alwaysShowError]
    );
    const hasError = Boolean(shouldShowError);
    const helperText = useMemo(() => 
        hasError ? error : helperTextProp,
    [hasError, error, helperTextProp]
    );

    const handleHelpIconClick = useCallback((event) => {
        setAnchorEl(prev => (prev ? null : event.currentTarget));
    }, []);

    const handlePopoverClose = useCallback(() => {
        setAnchorEl(null);
    }, []);
    const isPopoverOpen = Boolean(anchorEl);
    
    const handlePopoverKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            handlePopoverClose();
            anchorEl?.focus();
        }
    }, [anchorEl, handlePopoverClose]);

    useEffect(() => {
        if (isPopoverOpen && helpContentRef.current) {
            helpContentRef.current.focus();
        }
    }, [isPopoverOpen]);

    const handleRetentionInfoClick = useCallback((event) => {
        setRetentionInfoAnchorEl(prev => (prev ? null : event.currentTarget));
    }, []);

    const handleRetentionInfoClose = useCallback(() => {
        setRetentionInfoAnchorEl(null);
    }, []);
    const isRetentionInfoOpen = Boolean(retentionInfoAnchorEl);
    
    const handleRetentionPopoverKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            handleRetentionInfoClose();
            retentionInfoAnchorEl?.focus();
        }
    }, [retentionInfoAnchorEl, handleRetentionInfoClose]);

    useEffect(() => {
        if (isRetentionInfoOpen && retentionContentRef.current) {
            retentionContentRef.current.focus();
        }
    }, [isRetentionInfoOpen]);

    return (
        <div className={classes.container}>
            <Card 
                variant='outlined' 
                color={hasError ? 'error' : 'primary'}
                className={`${classes.card} ${hasError && (touched || alwaysShowError) ? classes.cardError : ''}`}
            >
                <CardContent className={classes.compactCardContent}>
                    <div className={classes.contentLayout}>
                        {/* Left side - Labels and info */}
                        <div className={classes.contentInfo}>
                            <Typography variant='body1' className={classes.titleText} id={`${nameProp}-label`}>
                                {hasError && (touched || alwaysShowError) && (
                                    <Icon
                                        name='alert-circle'
                                        className={classes.titleErrorIcon}
                                        aria-hidden='true'
                                    />
                                )}
                                <span id={`${nameProp}-label-text`}>
                                    {label}
                                </span>
                                {required && (
                                    <>
                                        <span aria-hidden='true' className={classes.requiredIndicator}>*</span>
                                        <span className={classes.visuallyHidden}>
                                            {formatMessage({
                                                id: 'uploadQuestionRequiredFieldTextId'
                                            })}
                                        </span>
                                    </>
                                )}
                                {question.showHelp && question.helpId && (
                                    <>
                                        <IconButton
                                            color='gray'
                                            onClick={handleHelpIconClick}
                                            aria-label={formatMessage({ id: question.helpId })}
                                            aria-haspopup='dialog'
                                            aria-expanded={isPopoverOpen}
                                            aria-controls={isPopoverOpen ? helpPopoverId : undefined}
                                            className={classes.helpIcon}
                                        >
                                            <Icon name='info' />
                                        </IconButton>
                                        <Popover
                                            open={isPopoverOpen}
                                            anchorEl={anchorEl}
                                            onClose={handlePopoverClose}
                                            id={helpPopoverId}
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'center' }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'center' }}
                                            onKeyDown={handlePopoverKeyDown}
                                            role='dialog'
                                            aria-modal='false'
                                            aria-labelledby={helpPopoverLabelId}
                                        >
                                            <Typography
                                                className={`${classes.helpPopoverText} ${classes.helpPopoverContent}`}
                                                component='div'
                                                tabIndex={-1}
                                                ref={helpContentRef}
                                                id={helpPopoverLabelId}
                                            >
                                                {formatMessage({ id: question.helpId })}
                                            </Typography>
                                        </Popover>
                                    </>
                                )}
                            </Typography>
                            
                            <Typography 
                                variant='body3' 
                                className={classes.infoText}
                                id={`${nameProp}-summary`}
                            >
                                {formatMessage({ id: 'uploadQuestionSummaryTextId' }, {
                                    size: question.fileSizeLimit || CONSTANTS.supplementalQuestionDetails.fileSizeLimit,
                                    formats: formatFileTypes(allowedTypes).toLowerCase()
                                })}
                            </Typography>
                        </div>
                        
                        {/* Right side - Upload button */}
                        <div className={classes.uploadSection}>
                            <input
                                {...rest}
                                id={`file-input-${nameProp}`}
                                ref={fileInputRef}
                                type='file'
                                multiple={maxFiles > 1}
                                className={classes.hiddenInput}
                                accept={getAcceptAttribute(allowedTypes)}
                                onChange={handleFileChange}
                                onBlur={onBlur}
                                onFocus={onFocus}
                                name={nameProp}
                                disabled={disabled || previewMode || hasUploadingFiles}
                                aria-labelledby={`${nameProp}-label-text`}
                                aria-describedby={hasError ? `${nameProp}-summary ${nameProp}-error` : `${nameProp}-summary`}
                                aria-required={required}
                                aria-invalid={hasError}
                            />
                            
                            <Button 
                                variant='outlined'
                                color='primary'
                                size='small'
                                className={classes.uploadButton}
                                disabled={disabled || previewMode || validFileCount >= maxFiles || hasUploadingFiles}
                                onClick={handleUploadClick}
                                aria-label={(() => {
                                    if (hasUploadingFiles) {
                                        return formatMessage({
                                            id: 'uploadDialogStatusUploading'
                                        });
                                    }
                                    if (validFileCount >= maxFiles) {
                                        return formatMessage(
                                            {
                                                id: 'uploadQuestionMaxFilesErrorTextId'
                                            },
                                            { max: maxFiles }
                                        );
                                    }
                                    return `${uploadButtonText} ${label}`.trim();
                                })()}
                                startIcon={<Icon name='upload' />}
                            >
                                {uploadButtonText}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Validation/helper text (errors like 'This is a required field') shown below the Card */}
            {hasError && (touched || alwaysShowError) && helperText && (
                <div className={classes.helperTextContainer} id={`${nameProp}-error`} role='alert' aria-live='assertive'>
                    <Typography variant='body3' className={classes.helperTextError}>
                        {helperText}
                    </Typography>
                </div>
            )}
            
            {/* File validation errors (size, type, max files, etc.) */}
            {errorMessages.length > 0 && (
                <div className={classes.errorContainer} role='alert' aria-live='polite' aria-atomic='true'>
                    {errorMessages.map((error, index) => (
                        <div key={index} className={classes.errorRow}>
                            <Typography 
                                variant='body3' 
                                className={classes.fileErrorText}
                            >
                                {error}
                            </Typography>
                            <IconButton
                                color='gray'
                                onClick={() => {
                                    setErrorMessages(prev => prev.filter((_, i) => i !== index));
                                }}
                                aria-label={`Dismiss error: ${error}`}
                                className={classes.errorCloseButton}
                            >
                                <Icon name='x' />
                            </IconButton>
                        </div>
                    ))}
                </div>
            )}

            {/* Display uploaded files as table rows below the card */}
            {uploadedFiles.length > 0 && (
                <section className={classes.fileListContainer} aria-labelledby={`${nameProp}-files-list`}>
                    <div className={classes.fileListHeaderContainer}>
                        <Typography 
                            variant='subtitle1'
                            component='p'
                            className={classes.fileListHeader}
                            id={`${nameProp}-files-list`}
                        >
                            {formatMessage({ id: 'uploadQuestionUploadedFilesTextId' }, { 
                                count: validFileCount, 
                                max: question.fileNumberLimit || CONSTANTS.supplementalQuestionDetails.fileDefaultLimit 
                            })}
                        </Typography>
                        <IconButton
                            color='gray'
                            onClick={handleRetentionInfoClick}
                            aria-label={formatMessage(
                                {
                                    id: 'uploadQuestionFileRetentionInfoTextId'
                                },
                                { days: fileRetentionDays }
                            )}
                            aria-haspopup='dialog'
                            aria-expanded={isRetentionInfoOpen}
                            aria-controls={isRetentionInfoOpen ? retentionPopoverId : undefined}
                            size='small'
                            className={classes.retentionInfoIcon}
                        >
                            <Icon name='info' />
                        </IconButton>
                        <Popover
                            open={isRetentionInfoOpen}
                            anchorEl={retentionInfoAnchorEl}
                            onClose={handleRetentionInfoClose}
                            id={retentionPopoverId}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center' }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center' }}
                            onKeyDown={handleRetentionPopoverKeyDown}
                            role='dialog'
                            aria-modal='false'
                            aria-labelledby={retentionPopoverLabelId}
                        >
                            <Typography
                                className={`${classes.helpPopoverText} ${classes.helpPopoverContent}`}
                                component='div'
                                tabIndex={-1}
                                ref={retentionContentRef}
                                id={retentionPopoverLabelId}
                            >
                                {formatMessage({ id: 'uploadQuestionFileRetentionInfoTextId' }, { 
                                    days: fileRetentionDays
                                })}
                            </Typography>
                        </Popover>
                    </div>
                    <p className={classes.visuallyHidden} id={`${nameProp}-files-summary`} aria-live='polite'>
                        {formatMessage({ id: 'uploadQuestionUploadedFilesTextId' }, { 
                            count: validFileCount, 
                            max: question.fileNumberLimit || CONSTANTS.supplementalQuestionDetails.fileDefaultLimit 
                        })}
                    </p>
                    <ul className={classes.fileList} aria-labelledby={`${nameProp}-files-list`}>
                        {uploadedFiles.map((file) => {
                            const fid = file?.id || `file_${Math.random().toString(36).slice(2,8)}`;
                            const fname = file?.name || fid;
                            const fsize = file?.size || 0;
                            const uploadProgressValue = uploadingFiles[fid];
                            const isUploading = typeof uploadProgressValue === 'number';
                            const isDeleting = deletingFiles[fid];
                            const fileUploadError = fileErrors[fid]?.message;
                            const isValidationError = file?.status === 'error' || String(fid).startsWith('error-');
                            const displayError = fileUploadError || file?.errorMessage;
                            const hasError = Boolean(displayError) || isValidationError;
                            const deleteLabelBase = hasError
                                ? formatMessage({ id: 'clear' })
                                : formatMessage({ id: 'delete'});
                            const deleteAriaLabel = `${deleteLabelBase} ${fname}`.trim();
                            const uploadingText = formatMessage({
                                id: 'uploadDialogStatusUploading'
                            });
                            const deletingBaseText = formatMessage({ id: 'delete' });
                            const deletingText = `${deletingBaseText}...`;
                            const deletingOutputLabel = `${deletingBaseText} ${fname}`.trim();

                            return (
                                <li key={fid} className={classes.fileListItem}>
                                    <div className={classes.fileRowLeft}>
                                        <div className={classes.fileRowIcon} aria-hidden='true'>
                                            <Icon name='file-text' />
                                        </div>
                                        <div className={classes.fileRowContent}>
                                            <span className={classes.fileRowTitle}>
                                                {isUploading ? uploadingText : fname}
                                            </span>
                                            {!isUploading && !displayError && fsize > 0 && (
                                                <span className={classes.fileRowMeta}>({formatFileSize(fsize)})</span>
                                            )}
                                        </div>
                                        {displayError && (
                                            <span className={classes.fileRowError} role='alert'>
                                                {displayError}
                                            </span>
                                        )}
                                    </div>
                                    <div className={classes.fileRowActions}>
                                        {isUploading ? (
                                            <Tooltip title={uploadingText} arrow placement='top'>
                                                <span
                                                    className={classes.uploadProgress}
                                                    aria-live='polite'
                                                    aria-atomic='true'
                                                >
                                                    <CircularProgress size={sizingMedium} aria-hidden='true' />
                                                    <span className={classes.visuallyHidden}>{`${uploadingText} ${fname}`}</span>
                                                </span>
                                            </Tooltip>
                                        ) : isDeleting ? (
                                            <Tooltip title={deletingText} arrow placement='top'>
                                                <span className={classes.uploadProgress} aria-label={deletingOutputLabel}>
                                                    <CircularProgress size={sizingMedium} aria-hidden='true' />
                                                </span>
                                            </Tooltip>
                                        ) : !previewMode ? (
                                            <Tooltip title={deleteLabelBase} arrow placement='top'>
                                                <IconButton
                                                    color='gray'
                                                    onClick={() => handleFileDelete(fid)}
                                                    disabled={disabled && !hasError}
                                                    aria-label={deleteAriaLabel}
                                                    size='large'
                                                    className={classes.deleteButtonUnboxed}
                                                    ref={el => {
                                                        if (el) {
                                                            deleteButtonRefs.current[fid] = el;
                                                        } else {
                                                            delete deleteButtonRefs.current[fid];
                                                        }
                                                    }}
                                                >
                                                    <Icon name={hasError ? 'close' : 'trash'} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            {/* Visually hidden live region for status announcements */}
            <output className={classes.visuallyHidden} aria-live='polite' aria-atomic='true'>
                {statusMessage}
            </output>

        </div>
    );
};

FormUploadQuestion.displayName = 'FormUploadQuestion';

export default FormUploadQuestion;